import { Address } from '@/form/types/address';
import { Form, FormProps, useSubmit } from 'react-router';
import { forwardRef } from 'react';
import { StripeElementsOptions } from '@stripe/stripe-js';
import {
  SubmitStripePaymentProps,
  useSubmitStripePayment,
} from '@/checkout/hooks/use-submit-stripe-payment';
import StripeProvider, {
  StripeProviderProps,
} from '@/checkout/providers/stripe-provider';

/**
 * Configuration that contains all the necessary props to submit a payment to Stripe.
 */
export interface StripeConfig
  extends Pick<StripeProviderProps, 'stripePromise' | 'availablePaymentMethods'>,
    SubmitStripePaymentProps {}

interface StripeCheckoutFormProps extends FormProps {
  stripeConfig?: StripeConfig;
  shouldUseStripe?: boolean;
  elementsOptions?: StripeElementsOptions;
  billingAddress?: Partial<Address>;
}

// TODO: RUBY-4412 -Refactor stripe checkout form to use card or ach or both with options pre configured
/**
 * Remix Form that handles Stripe payments. If a Stripe client secret is provided, this component will
 * submit the payment to Stripe before submitting the form data to the server. When a Stripe client secret
 * is not provided, this should behave like a normal Remix Form.
 *
 * @see https://docs.stripe.com/payments/finalize-payments-on-the-server?platform=web&type=setup#create-ct
 */
const StripeCheckoutForm = forwardRef<HTMLFormElement, StripeCheckoutFormProps>(
  (
    {
      method = 'post',
      onSubmit,
      children,
      stripeConfig,
      billingAddress,
      ...props
    }: StripeCheckoutFormProps,
    ref
  ) => {
    const submit = useSubmit();
    const submitStripe = useSubmitStripePayment({
      ...stripeConfig,
      billingAddress,
    });

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
      // Only execute custom submit logic if a Stripe submit callback exists.
      // The Stripe submit callback does not initialize if there is no payment element in the form.
      if (submitStripe) {
        event.preventDefault();

        let formData = new FormData(event.currentTarget);
        // Submit button intent is not included in the form data as initialized from the event.currentTarget.
        // Check if there is a submit button with an intent, append it to the FormData if applicable.
        const submitter = (event.nativeEvent as SubmitEvent).submitter;
        if (submitter && submitter instanceof HTMLButtonElement) {
          const { name, value } = submitter;

          if (name && value) {
            formData.append(submitter.name, submitter.value);
          }
        }

        if (stripeConfig) {
          // Submit elements to Stripe and append user's payment method to formData
          formData = await submitStripe(formData);
        }
        if (onSubmit) onSubmit(event);

        submit(formData, { method });
      }
    };

    return (
      <Form ref={ref} method={method} onSubmit={handleSubmit} {...props}>
        {children}
      </Form>
    );
  }
);

StripeCheckoutForm.displayName = 'StripeCheckoutForm';

/**
 * Fully functioning Stripe Checkout Form that combines the StripeProvider and StripeCheckoutForm components.
 */
export default function CheckoutFormWithProvider({
  children,
  stripeConfig,
  shouldUseStripe = false,
  method = 'post',
  elementsOptions,
  billingAddress,
  ...props
}: StripeCheckoutFormProps) {
  const { stripePromise, customerSessionClientSecret, availablePaymentMethods } =
    stripeConfig ?? {};

  if (!shouldUseStripe) {
    return (
      <Form method={method} {...props}>
        {children}
      </Form>
    );
  }

  return (
    <StripeProvider
      stripePromise={stripePromise}
      customerSessionClientSecret={customerSessionClientSecret}
      availablePaymentMethods={availablePaymentMethods}
      elementsOptions={elementsOptions}
    >
      <StripeCheckoutForm
        stripeConfig={stripeConfig}
        billingAddress={billingAddress}
        {...props}
      >
        {children}
      </StripeCheckoutForm>
    </StripeProvider>
  );
}
